/**
 * The components that drive the section settings, call-to-action
 * settings, etc.
 */
import { Button } from '@components/buttons';
import { IcoChevronRight, IcoTrash } from '@components/icons';
import { ComponentChildren } from 'preact';
import { Dispatch, StateUpdater } from 'preact/hooks';
import type {
  BackgroundType,
  BorderRadius,
  BorderWidth,
  HoverLift,
  IconShape,
  PaddingSize,
  SalesBlockState,
  ShadowSize,
} from 'server/types';
import { RUZUKU_ASSETS_BASE_URL } from 'shared/consts';
import { BULLET_STYLE_OPTIONS, BulletIcon, BulletStyle } from '../bullet-styles';
import { autoColor } from 'shared/colors';
import { ImageOptions, MenuChoice, MenuHR, MenuHeader, MenuItem, SettingsMenu } from './core';
import { MenuPane, MenuProps } from './types';
import { ColorOptions } from './color-options';

export const defaultBgImg = `${RUZUKU_ASSETS_BASE_URL}/bg-rainbow-gradient.jpg?width=3276`;

const srcs = [
  `${RUZUKU_ASSETS_BASE_URL}/bg-rainbow-gradient.jpg?width=3276`,
  `${RUZUKU_ASSETS_BASE_URL}/bg-paint.jpg?width=3276`,
  `${RUZUKU_ASSETS_BASE_URL}/bg-purple-stripes.jpg?width=3276`,
  `${RUZUKU_ASSETS_BASE_URL}/bg-orange-stripes.jpg?width=3276`,
  `${RUZUKU_ASSETS_BASE_URL}/bg-blue-to-pink.jpg?width=3276`,
];

function BackgroundPane(props: MenuProps) {
  return (
    <>
      <MenuHeader label="Section background" close={props.close} />
      <div class="flex flex-col gap-2 py-2">
        <ColorOptions
          label="Background"
          name="bgcolor"
          value={props.state.bgcolor}
          onChange={(bgcolor) =>
            props.setState((s) => ({ ...s, bgcolor, fgcolor: autoColor(bgcolor) }))
          }
        />
        <ColorOptions
          label="Text"
          name="fgcolor"
          value={props.state.fgcolor}
          onChange={(fgcolor) => props.setState((s) => ({ ...s, fgcolor }))}
        />
        <ImageOptions
          values={srcs}
          value={props.state.bgimg}
          onChange={(bgimg) => props.setState((s) => ({ ...s, bgimg }))}
        />
      </div>
    </>
  );
}

function PaddingPane(props: MenuProps) {
  const choices: PaddingSize[] = ['sm', 'md', 'lg', 'xl'];
  return (
    <>
      <MenuHeader label="Padding" close={props.close} />
      <div class="flex flex-col gap-2">
        <MenuChoice
          label="Top"
          name="paddingt"
          choices={choices}
          choiceUi={(x) => x.toUpperCase()}
          value={props.state.paddingt}
          onChange={(value) => props.setState((s) => ({ ...s, paddingt: value as PaddingSize }))}
        />
        <MenuChoice
          label="Bottom"
          name="paddingb"
          choiceUi={(x) => x.toUpperCase()}
          choices={choices}
          value={props.state.paddingb}
          onChange={(value) => props.setState((s) => ({ ...s, paddingb: value as PaddingSize }))}
        />
      </div>
    </>
  );
}

function CardStylePane(props: MenuProps) {
  const { state, setState } = props;
  const { cardStyles, iconStyles } = state;
  const borderRadiusOpts: BorderRadius[] = ['none', 'sm', 'md', 'lg', 'xl', '2xl'];
  const shadowOpts: ShadowSize[] = ['none', 'sm', 'md', 'lg', 'xl'];
  const borderWidthOpts: BorderWidth[] = ['none', 'thin', 'medium', 'thick'];
  const hoverLifts: HoverLift[] = ['none', 'sm', 'lg'];
  const backgroundTypes: BackgroundType[] = ['solid', 'gradient'];
  const backgroundType: BackgroundType = cardStyles?.backgroundType || 'solid';
  const iconShapes: IconShape[] = ['circle', 'rounded', 'square'];
  const borderWidths: BorderWidth[] = ['none', 'thin', 'medium', 'thick'];
  const borderWidthLabels: string[] = ['None', 'SM', 'MD', 'LG'];

  return (
    <>
      <MenuHeader label="Card style" close={props.close} />
      <div class="flex flex-col gap-4">
        <MenuChoice
          label="Border width"
          name="cardborder"
          choices={borderWidthOpts}
          value={cardStyles?.borderWidth}
          choiceUi={(x) => borderWidthLabels[borderWidths.indexOf(x)]}
          onChange={(borderWidth) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, borderWidth } }))
          }
        />
        <ColorOptions
          label="Border color"
          name="cardbordercolor"
          value={cardStyles?.borderColor}
          onChange={(borderColor) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, borderColor } }))
          }
        />
        <MenuHR />
        <MenuChoice
          label="Background"
          name="cardbg"
          choices={backgroundTypes}
          value={backgroundType}
          onChange={(backgroundType) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, backgroundType } }))
          }
        />
        {backgroundType === 'solid' && (
          <ColorOptions
            label="Background color"
            name="cardbg"
            value={cardStyles?.backgroundColor}
            onChange={(backgroundColor) =>
              setState((s) => ({
                ...s,
                cardStyles: {
                  ...s.cardStyles,
                  backgroundColor,
                },
              }))
            }
          />
        )}
        {backgroundType === 'gradient' && (
          <>
            <ColorOptions
              label="Gradient from"
              name="cardgradientfrom"
              value={cardStyles?.gradientFrom}
              onChange={(gradientFrom) =>
                setState((s) => ({
                  ...s,
                  cardStyles: {
                    ...s.cardStyles,
                    gradientFrom,
                  },
                }))
              }
            />
            <ColorOptions
              label="Gradient to"
              name="cardgradientto"
              value={cardStyles?.gradientTo}
              onChange={(gradientTo) =>
                setState((s) => ({
                  ...s,
                  cardStyles: {
                    ...s.cardStyles,
                    gradientTo,
                  },
                }))
              }
            />
          </>
        )}
        <MenuHR />
        <MenuChoice
          label="Rounding"
          name="cardradius"
          choices={borderRadiusOpts}
          value={cardStyles?.borderRadius}
          choiceUi={(x) => (x === 'none' ? x : x.toUpperCase())}
          onChange={(borderRadius) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, borderRadius } }))
          }
        />
        <MenuChoice
          label="Shadow"
          name="cardshadow"
          choices={shadowOpts}
          value={cardStyles?.shadow}
          choiceUi={(x) => (x === 'none' ? x : x.toUpperCase())}
          onChange={(shadow) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, shadow } }))
          }
        />
        <MenuChoice
          label="Hover lift"
          name="hoverlift"
          choices={hoverLifts}
          value={cardStyles?.hoverLift}
          choiceUi={(x) => (x === 'none' ? x : x.toUpperCase())}
          onChange={(hoverLift) =>
            setState((s) => ({ ...s, cardStyles: { ...s.cardStyles, hoverLift } }))
          }
        />

        {iconStyles && (
          <>
            <MenuHR />
            <MenuChoice
              label="Icons"
              name="icons"
              choices={iconShapes}
              value={iconStyles.shape}
              onChange={(shape) =>
                setState((s) => ({ ...s, iconStyles: { ...s.iconStyles, shape } }))
              }
            />
            <ColorOptions
              label="Icon background"
              name="iconbg"
              value={iconStyles.backgroundColor}
              onChange={(backgroundColor) =>
                setState((s) => ({
                  ...s,
                  iconStyles: {
                    ...s.iconStyles,
                    backgroundColor,
                  },
                }))
              }
            />
            <MenuChoice
              label="Icon border"
              name="iconborder"
              choices={borderWidths}
              choiceUi={(choice) => {
                switch (choice) {
                  case 'thin':
                    return 'sm';
                  case 'medium':
                    return 'md';
                  case 'thick':
                    return 'lg';
                  default:
                    return choice;
                }
              }}
              value={iconStyles.borderWidth}
              onChange={(borderWidth) =>
                setState((s) => ({ ...s, iconStyles: { ...s.iconStyles, borderWidth } }))
              }
            />
            <ColorOptions
              label="Icon border color"
              name="iconbordercolor"
              value={iconStyles.borderColor}
              onChange={(borderColor) =>
                setState((s) => ({
                  ...s,
                  iconStyles: {
                    ...s.iconStyles,
                    borderColor,
                  },
                }))
              }
            />
          </>
        )}
      </div>
    </>
  );
}

function BulletStylePane(props: MenuProps) {
  const { state, setState } = props;
  const choices = BULLET_STYLE_OPTIONS.map((x) => x.value);
  return (
    <>
      <MenuHeader label="Bullet style" close={props.close} />
      <div class="flex flex-col gap-3">
        <MenuChoice
          label="Shape"
          choices={choices}
          name="bulletStyle"
          value={state.bulletStyle}
          choiceUi={(value) => {
            return <BulletIcon style={value as BulletStyle} class="size-5 shrink-0" />;
          }}
          onChange={(bulletStyle) => props.setState((s) => ({ ...s, bulletStyle }))}
        />
        <ColorOptions
          name="bulletcolor"
          value={state.bulletColor}
          onChange={(bulletColor) => setState((s) => ({ ...s, bulletColor }))}
        />
      </div>
    </>
  );
}

function SubMenuTrigger(props: MenuProps & { label: ComponentChildren; pane: MenuPane }) {
  return (
    <Button
      class="flex items-center justify-between p-1.5 px-2 gap-4 text-sm hover:bg-gray-100 rounded-md transition-all"
      onClick={() => props.pushPane(props.pane)}
    >
      <span>{props.label}</span>
      <IcoChevronRight />
    </Button>
  );
}

function SectionSettingsPane(props: MenuProps) {
  return (
    <>
      <MenuHeader label="Section settings" close={props.close} isBase />
      <SubMenuTrigger {...props} label="Padding" pane={PaddingPane} />
      <SubMenuTrigger {...props} label="Background" pane={BackgroundPane} />
      <SubMenuTrigger {...props} label="Bullet style" pane={BulletStylePane} />
      {props.state.cardStyles && (
        <SubMenuTrigger {...props} label="Card style" pane={CardStylePane} />
      )}
      <MenuHR />
      <footer class="pt-2">
        <MenuItem label="Delete section">
          <Button
            class="hover:bg-red-100 rounded-md p-2 bg-red-50"
            title="Delete section"
            onClick={props.onDelete}
          >
            <IcoTrash class="text-red-600 size-4" />
          </Button>
        </MenuItem>
      </footer>
    </>
  );
}

export function SectionSettingsMenu(opts: {
  state: SalesBlockState;
  setState: Dispatch<StateUpdater<SalesBlockState>>;
  close(): void;
  onDelete(): void;
}) {
  return <SettingsMenu {...opts} position="bottom" basePane={SectionSettingsPane} />;
}
